import { RiskShareBarList, type RiskShareRow } from "./RiskShareBarList";
import { getWorstRiskForAsset, type AssetHazardResult } from "@/lib/results";
import type { Asset, ValueChainStage } from "@/lib/assets";

interface ValueChainRiskPanelProps {
  assets: Asset[];
  stages: ValueChainStage[];
  results: AssetHazardResult[];
}

/**
 * Per-stage share of assets at high risk — counts distinct assets (an asset
 * counts once if ANY of its hazards reaches risk 3-4), same rule as the
 * portfolio-level "assets at high risk" framing, just grouped by value
 * chain stage instead of by hazard. This is the one company-actionable
 * question the hazard-only breakdown (HazardRiskBarChart) can't answer on
 * its own: which part of the value chain — not which hazard — needs
 * climate resilience investment first.
 */
function valueChainStageRows(assets: Asset[], stages: ValueChainStage[], results: AssetHazardResult[]): RiskShareRow[] {
  const stageNameById = new Map(stages.map((s) => [s.id, s.name]));

  const assetsByStage = new Map<string, Asset[]>();
  for (const asset of assets) {
    if (!asset.valueChainStageId) continue;
    const existing = assetsByStage.get(asset.valueChainStageId);
    if (existing) existing.push(asset);
    else assetsByStage.set(asset.valueChainStageId, [asset]);
  }

  return Array.from(assetsByStage.entries()).map(([stageId, stageAssets]) => {
    let hi = 0;
    let tot = 0;
    for (const asset of stageAssets) {
      const worst = getWorstRiskForAsset(asset.id, results);
      if (worst === null) continue;
      tot++;
      if (worst >= 3) hi++;
    }
    return { label: stageNameById.get(stageId) ?? "Unmapped stage", hi, tot };
  });
}

export function ValueChainRiskPanel({ assets, stages, results }: ValueChainRiskPanelProps) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="mb-3 text-sm font-semibold">Share of assets at high risk, by value chain stage</p>
      <RiskShareBarList
        rows={valueChainStageRows(assets, stages, results)}
        emptyMessage="Not available yet — no value chain stage has computed risk scores"
      />
    </div>
  );
}
